import type {
  LegendListProps,
  LegendListRef,
} from '@legendapp/list/react-native';
import { useLocalSearchParams } from 'expo-router';
import type { Ref } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useSubscription } from 'urql';
import LegendList from '@/components/Custom/LegendList';
import Placeholder from '@/components/Custom/Placeholder';
import Box from '@/components/UI/Box';
import { graphql, type ResultOf } from '@/gql/graphql';
import { EventScorekeepingFeedItemFragment } from './EventScorekeepingFeedItem';

const LIMIT = 1000;

export const GetEventScorekeepingFeed = graphql(/* GraphQL */ `
  query GetEventScorekeeping($eventId: uuid!, $limit: Int!) {
    badge_assignee(
      where: {
        event_id: { _eq: $eventId }
        order: { _is_null: true }
        badge: { is_stat: { _eq: true } }
      }
      order_by: [
        { event_segment: { order: desc_nulls_last } }
        { elapsed_segment_ms: desc_nulls_last }
        { created_at: desc }
      ]
      limit: $limit
    ) {
      id
      badge_id
      person_id
      event_id
      team_id
      badge_assignees(order_by: { order: asc_nulls_first }) {
        ...EventScorekeepingFeedItem
      }
    }
  }
`, [EventScorekeepingFeedItemFragment]);

const WatchEventScorekeeping = graphql(/* GraphQL */ `
  subscription WatchEventScorekeeping($eventId: uuid!, $limit: Int!) {
    badge_assignee(
      where: {
        event_id: { _eq: $eventId }
        order: { _is_null: true }
        badge: { is_stat: { _eq: true } }
      }
      order_by: [
        { event_segment: { order: desc_nulls_last } }
        { elapsed_segment_ms: desc_nulls_last }
        { created_at: desc }
      ]
      limit: $limit
    ) {
      id
      badge_id
      person_id
      event_id
      team_id
      badge_assignees(order_by: { order: asc_nulls_first }) {
        ...EventScorekeepingFeedItem
      }
    }
  }
`, [EventScorekeepingFeedItemFragment]);

type EventScorekeepingFeedRow = ResultOf<
  typeof GetEventScorekeepingFeed
>['badge_assignee'][number];

type EventScorekeepingFeedProps = {
  renderItem: NonNullable<
    LegendListProps<EventScorekeepingFeedRow>['renderItem']
  >;
  extraData?: LegendListProps<EventScorekeepingFeedRow>['extraData'];
  ref?: Ref<LegendListRef>;
};

function EventScorekeepingFeed({
  renderItem,
  extraData,
  ref,
}: EventScorekeepingFeedProps) {
  const { eventId: eventIdParam } = useLocalSearchParams<{
    eventId?: string;
  }>();
  const eventId = eventIdParam || '';

  const [{ data, fetching }] = useQuery({
    query: GetEventScorekeepingFeed,
    variables: {
      eventId,
      limit: LIMIT,
    },
    pause: !eventId,
  });

  useSubscription({
    query: WatchEventScorekeeping,
    variables: {
      eventId,
      limit: LIMIT,
    },
    pause: !eventId,
  });

  if (!fetching && data?.badge_assignee?.length === 0) {
    return (
      <Placeholder
        text="No stats have been recorded here yet."
        icon="list-outline"
      />
    );
  }
  return (
    <Box style={style.container}>
      <LegendList
        extraData={extraData}
        contentContainerStyle={style.content}
        ref={ref}
        // biome-ignore lint/suspicious/noTsIgnore: below
        /* @ts-ignore - Types fixed in upcoming unistyles release */
        keyExtractor={(item) => item.id}
        style={style.list}
        data={data?.badge_assignee || []}
        // biome-ignore lint/suspicious/noTsIgnore: below
        /* @ts-ignore - Types fixed in upcoming unistyles release */
        renderItem={renderItem}
      />
    </Box>
  );
}

export default EventScorekeepingFeed;

const style = StyleSheet.create((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  list: {
    flex: 1,
    width: '100%',
  },
  content: {
    paddingVertical: theme.size(1),
  },
}));
